import { DefaultButton, PrimaryButton } from "@fluentui/react";
import PanelActions, {
  commitButtonStyle,
} from "components/shared/panel/PanelActions";
import PanelMessenger from "components/shared/panel/PanelMessenger";
import { PolicyEditState } from "models/policyPanel";
import { RevisionStatus } from "models/revisions";
import useRouter from "hooks/useRouter";
import { routeDefinitions } from "router";
import { v1 as uuidv1 } from 'uuid';
import { useCreatePolicyMutation } from "services/policy";
import { environmentConfig } from "config/environmentConfig";

export type PolicyPanelActionsProps = {
  editState: PolicyEditState;
  onClosePanel: () => void;
  formValues: {
    policyType: string;
    policyName: string;
    description: string;
  };
};

// eslint-disable-next-line react/function-component-definition
function PolicyPanelActions({
  editState,
  onClosePanel,
  formValues,
}: PolicyPanelActionsProps) {
  const isNewPolicy = editState === PolicyEditState.NewPolicy;
  const { goToRoute } = useRouter();
  const [createPolicy, { isLoading: isSubmitting }] = useCreatePolicyMutation();

  const handleCreatePolicy = async () => {
    if (!formValues?.policyName) {
      // TODO: Show validation error
      return;
    }
    
    try {
      // Generate a proper GUID for the policy ID
      const newPolicyId = uuidv1();
      
      // Call the API to create the policy
      const result = await createPolicy({
        id: newPolicyId,
        type: formValues.policyType,
        name: formValues.policyName,
        description: formValues.description,
        status: RevisionStatus.Draft,
        cloud: environmentConfig.cloud,
        environment: environmentConfig.environment,
        policyType: formValues.policyType
      }).unwrap();
      
      if (result.success) {
        // Close the panel first
        onClosePanel();
        
        // Navigate to the PolicyEdit page for template association
        goToRoute(
          routeDefinitions.PolicyEdit.getRouteInfo({
            policyId: newPolicyId,
          })
        );
      }
    } catch (error) {
      // TODO: Handle error - show error message to user
    }
  };

  const handleSavePolicy = () => {
    // TODO: Implement policy save logic
    onClosePanel();
  };

  const handleCancel = () => {
    onClosePanel();
  };

  return (
    <PanelActions>
      <PanelMessenger isSubmitting={isSubmitting} />
      <div style={{ display: "flex", justifyContent: "flex-start", gap: "12px" }}>
        {isNewPolicy ? (
          <PrimaryButton
            data-automation-id="panel-saveButton"
            text="Create"
            onClick={handleCreatePolicy}
            disabled={isSubmitting || !formValues.policyName || !formValues.policyType}
            style={commitButtonStyle}
          />
        ) : (
          <PrimaryButton
            data-automation-id="panel-saveButton"
            text="Save"
            onClick={handleSavePolicy}
            disabled={isSubmitting || !formValues.policyName || !formValues.policyType}
            style={commitButtonStyle}
          />
        )}
        <DefaultButton
          data-automation-id="panel-cancelButton"
          text="Cancel"
          onClick={handleCancel}
          disabled={isSubmitting}
        />
      </div>
    </PanelActions>
  );
}

export default PolicyPanelActions;